'use client';

import { useState, useMemo } from 'react';
import { BLOG_ARTICLES, BLOG_CATEGORIES, type Article } from '../data/articles';
import SearchFiltersSection from './SearchFiltersSection';
import BlogArticleGrid from './BlogArticleGrid';
import PaginationSection from './PaginationSection';

const PER_PAGE = 6;

function filterArticles(
  articles: Article[],
  category: string,
  searchQuery: string
): Article[] {
  let result = articles;

  if (category && category !== 'All') {
    result = result.filter((a) => a.category === category);
  }

  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    result = result.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.author.toLowerCase().includes(q)
    );
  }

  return result;
}

export default function BlogContent() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = useMemo(
    () => filterArticles(BLOG_ARTICLES, selectedCategory, searchQuery),
    [selectedCategory, searchQuery]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageIndex = Math.min(currentPage, totalPages);
  const start = (pageIndex - 1) * PER_PAGE;
  const articlesForPage = filtered.slice(start, start + PER_PAGE);

  // Reset to page 1 when filters change (handled by useMemo - we need to reset currentPage when category/search changes)
  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };
  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    setCurrentPage(1);
  };

  return (
    <>
      <SearchFiltersSection
        categories={BLOG_CATEGORIES}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchChange}
      />
      <BlogArticleGrid articles={articlesForPage} />
      <PaginationSection
        currentPage={pageIndex}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </>
  );
}
